import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Paper,
    Typography,
    TextField,
    Button,
    Container,
    Alert,
    Snackbar,
    Stack,
    Divider,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton
} from '@mui/material';
import { Send as SendIcon, Cancel as CancelIcon, Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { sendEventApprovalRequest } from '../services/email';
import { getClientPrincipal } from '../services/auth';
import { submitEventForm } from '../services/eventForms';

interface Participant {
    name: string;
    workstream: string;
    roleInterest: string;
    fundingSource: string;
}

interface CostItem {
    category: string;
    details: string;
    estimatedCost: string;
}

interface EventFormData {
    // Section 1: Event Details
    eventTitle: string;
    dates: string;
    location: string;
    eventOrganisers: string;
    typeOfEvent: string;
    eventOverview: string;
    
    // Section 2: Rationale for Participation
    relevanceToIOD: string;
    expectedBenefits: string;
    proposedOutputs: string;
    
    // Section 3: Participants
    participants: Participant[];
    
    // Section 4: Estimated Costs
    costs: CostItem[];
    
    // Section 5: Communications and Follow up
    plannedCommunications: string;
    leadForCommunications: string;
    followUpActions: string;
}

const EventFormPage = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState<EventFormData>({
        eventTitle: '',
        dates: '',
        location: '',
        eventOrganisers: '',
        typeOfEvent: '',
        eventOverview: '',
        relevanceToIOD: '',
        expectedBenefits: '',
        proposedOutputs: '',
        participants: [{ name: '', workstream: '', roleInterest: '', fundingSource: '' }],
        costs: [
            { category: 'Airfare', details: '', estimatedCost: '' },
            { category: 'Transfers', details: '', estimatedCost: '' },
            { category: 'Accommodation', details: '', estimatedCost: '' },
            { category: 'Subsistence', details: '', estimatedCost: '' },
            { category: 'Registration Fees', details: '', estimatedCost: '' },
            { category: 'Other', details: '', estimatedCost: '' }
        ],
        plannedCommunications: '',
        leadForCommunications: '',
        followUpActions: ''
    });

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const addParticipant = () => {
        setFormData(prev => ({
            ...prev,
            participants: [...prev.participants, { name: '', workstream: '', roleInterest: '', fundingSource: '' }]
        }));
    };

    const removeParticipant = (index: number) => {
        if (formData.participants.length > 1) {
            setFormData(prev => ({
                ...prev,
                participants: prev.participants.filter((_, i) => i !== index)
            }));
        }
    };

    const updateParticipant = (index: number, field: keyof Participant, value: string) => {
        setFormData(prev => ({
            ...prev,
            participants: prev.participants.map((p, i) => 
                i === index ? { ...p, [field]: value } : p
            )
        }));
    };

    const updateCost = (index: number, field: keyof CostItem, value: string) => {
        setFormData(prev => ({
            ...prev,
            costs: prev.costs.map((c, i) => 
                i === index ? { ...c, [field]: value } : c
            )
        }));
    };

    const calculateTotalCost = (): string => {
        const total = formData.costs.reduce((sum, cost) => {
            const amount = parseFloat(cost.estimatedCost.replace(/[^0-9.-]/g, '')) || 0;
            return sum + amount;
        }, 0);
        return total.toFixed(2);
    };

    const handleSubmit = async () => {
        // Validate required fields
        if (!formData.eventTitle.trim()) {
            setSnackbarMessage('Please provide an Event Title');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        if (!formData.dates.trim()) {
            setSnackbarMessage('Please provide Event Dates');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        if (!formData.location.trim()) {
            setSnackbarMessage('Please provide Event Location');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
            return;
        }

        setIsSubmitting(true);

        try {
            // Get user info
            const principal = await getClientPrincipal();
            const submitterEmail = principal?.userDetails || 'Unknown';
            const submitterName = principal?.name || 'Unknown';

            // Calculate total cost
            const totalCostValue = parseFloat(calculateTotalCost()) || 0;

            // Save to database
            await submitEventForm({
                submitter_email: submitterEmail,
                submitter_name: submitterName,
                status: 'pending',
                event_title: formData.eventTitle,
                dates: formData.dates,
                location: formData.location,
                event_organisers: formData.eventOrganisers || undefined,
                type_of_event: formData.typeOfEvent || undefined,
                event_overview: formData.eventOverview || undefined,
                relevance_to_iod: formData.relevanceToIOD || undefined,
                expected_benefits: formData.expectedBenefits || undefined,
                proposed_outputs: formData.proposedOutputs || undefined,
                participants: JSON.stringify(formData.participants.filter(p => p.name || p.workstream || p.roleInterest || p.fundingSource)),
                costs: JSON.stringify(formData.costs.filter(c => c.details || c.estimatedCost)),
                total_cost: totalCostValue,
                planned_communications: formData.plannedCommunications || undefined,
                lead_for_communications: formData.leadForCommunications || undefined,
                follow_up_actions: formData.followUpActions || undefined
            });

            // Send email notification
            await sendEventApprovalRequest({
                ...formData,
                submitterEmail,
                submitterName,
                totalCost: calculateTotalCost()
            });

            setSnackbarMessage('Event approval request submitted successfully. Your request has been sent for review.');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);

            // Navigate to home after short delay
            setTimeout(() => {
                navigate('/');
            }, 1500);
        } catch (error) {
            console.error('Error submitting event approval request:', error);
            setSnackbarMessage('An error occurred while submitting the request.');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate('/');
    };

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Paper elevation={3} sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ color: '#009688', mb: 1 }}>
                    External Event Approval Form
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                    To be completed for all external conferences, workshops or events requiring participation approval.
                </Typography>

                <Stack spacing={4}>
                    {/* Section 1: Event Details */}
                    <Box>
                        <Typography variant="h6" sx={{ color: '#009688', mb: 2, fontWeight: 600 }}>
                            Section 1: Event Details
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                required
                                label="Event Title"
                                value={formData.eventTitle}
                                onChange={(e) => setFormData(prev => ({ ...prev, eventTitle: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                required
                                label="Dates"
                                type="date"
                                value={formData.dates}
                                onChange={(e) => setFormData(prev => ({ ...prev, dates: e.target.value }))}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                            <TextField
                                fullWidth
                                required
                                label="Location (Venue/City/Country)"
                                value={formData.location}
                                onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                label="Event Organiser(s)"
                                value={formData.eventOrganisers}
                                onChange={(e) => setFormData(prev => ({ ...prev, eventOrganisers: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                label="Type of Event"
                                value={formData.typeOfEvent}
                                onChange={(e) => setFormData(prev => ({ ...prev, typeOfEvent: e.target.value }))}
                                placeholder="Conference, Workshop, Roundtable, etc."
                            />
                            <TextField
                                fullWidth
                                label="Event Overview"
                                multiline
                                rows={4}
                                value={formData.eventOverview}
                                onChange={(e) => setFormData(prev => ({ ...prev, eventOverview: e.target.value }))}
                                placeholder="Brief description and relevance"
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Section 2: Rationale for Participation */}
                    <Box>
                        <Typography variant="h6" sx={{ color: '#009688', mb: 2, fontWeight: 600 }}>
                            Section 2: Rationale for Participation
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Relevance to IOD PARC / Workstreams / BGT"
                                multiline
                                rows={4}
                                value={formData.relevanceToIOD}
                                onChange={(e) => setFormData(prev => ({ ...prev, relevanceToIOD: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                label="Expected Benefits"
                                multiline
                                rows={4}
                                value={formData.expectedBenefits}
                                onChange={(e) => setFormData(prev => ({ ...prev, expectedBenefits: e.target.value }))}
                                placeholder="e.g., Networking, Thought Leadership, Learning, etc."
                            />
                            <TextField
                                fullWidth
                                label="Proposed Outputs"
                                multiline
                                rows={4}
                                value={formData.proposedOutputs}
                                onChange={(e) => setFormData(prev => ({ ...prev, proposedOutputs: e.target.value }))}
                                placeholder="e.g., LinkedIn post, blog, internal learning note, etc."
                            />
                        </Stack>
                    </Box>

                    <Divider />

                    {/* Section 3: Participants */}
                    <Box>
                        <Typography variant="h6" sx={{ color: '#009688', mb: 2, fontWeight: 600 }}>
                            Section 3: Participants
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Workstream</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Role/Interest</TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>Funding Source</TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: 50 }}>Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {formData.participants.map((participant, index) => (
                                        <TableRow key={index}>
                                            <TableCell>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={participant.name}
                                                    onChange={(e) => updateParticipant(index, 'name', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={participant.workstream}
                                                    onChange={(e) => updateParticipant(index, 'workstream', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={participant.roleInterest}
                                                    onChange={(e) => updateParticipant(index, 'roleInterest', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={participant.fundingSource}
                                                    onChange={(e) => updateParticipant(index, 'fundingSource', e.target.value)}
                                                    placeholder="e.g., central BD funding pool"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    size="small"
                                                    onClick={() => removeParticipant(index)}
                                                    disabled={formData.participants.length === 1}
                                                    color="error"
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                        <Button
                            startIcon={<AddIcon />}
                            onClick={addParticipant}
                            sx={{ mt: 1, color: '#009688' }}
                        >
                            Add Participant
                        </Button>
                    </Box>

                    <Divider />

                    {/* Section 4: Estimated Costs */}
                    <Box>
                        <Typography variant="h6" sx={{ color: '#009688', mb: 2, fontWeight: 600 }}>
                            Section 4: Estimated Costs
                        </Typography>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: 600, width: '25%' }}>Category</TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: '50%' }}>Details</TableCell>
                                        <TableCell sx={{ fontWeight: 600, width: '25%' }}>Estimated Cost (£)</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {formData.costs.map((cost, index) => (
                                        <TableRow key={index}>
                                            <TableCell>{cost.category}</TableCell>
                                            <TableCell>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={cost.details}
                                                    onChange={(e) => updateCost(index, 'details', e.target.value)}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    fullWidth
                                                    size="small"
                                                    value={cost.estimatedCost}
                                                    onChange={(e) => updateCost(index, 'estimatedCost', e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow>
                                        <TableCell colSpan={2} sx={{ fontWeight: 600, textAlign: 'right' }}>
                                            Total Estimated Cost
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: 600 }}>
                                            £{calculateTotalCost()}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>

                    <Divider />

                    {/* Section 5: Communications and Follow up */}
                    <Box>
                        <Typography variant="h6" sx={{ color: '#009688', mb: 2, fontWeight: 600 }}>
                            Section 5: Communications and Follow up
                        </Typography>
                        <Stack spacing={2}>
                            <TextField
                                fullWidth
                                label="Planned Communications Outputs"
                                multiline
                                rows={4}
                                value={formData.plannedCommunications}
                                onChange={(e) => setFormData(prev => ({ ...prev, plannedCommunications: e.target.value }))}
                                placeholder="e.g., post-event blog, LinkedIn updates, internal note"
                            />
                            <TextField
                                fullWidth
                                label="Lead for Communications"
                                value={formData.leadForCommunications}
                                onChange={(e) => setFormData(prev => ({ ...prev, leadForCommunications: e.target.value }))}
                            />
                            <TextField
                                fullWidth
                                label="Follow-up Actions / Reporting"
                                multiline
                                rows={4}
                                value={formData.followUpActions}
                                onChange={(e) => setFormData(prev => ({ ...prev, followUpActions: e.target.value }))}
                            />
                        </Stack>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    {/* Action Buttons */}
                    <Stack direction="row" spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
                        <Button
                            variant="outlined"
                            startIcon={<CancelIcon />}
                            onClick={handleCancel}
                            disabled={isSubmitting}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SendIcon />}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            sx={{
                                bgcolor: '#009688',
                                '&:hover': {
                                    bgcolor: '#00796b'
                                }
                            }}
                        >
                            {isSubmitting ? 'Submitting...' : 'Submit for Approval'}
                        </Button>
                    </Stack>
                </Stack>
            </Paper>

            {/* Snackbar for notifications */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={6000}
                onClose={() => setSnackbarOpen(false)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbarOpen(false)}
                    severity={snackbarSeverity}
                    sx={{ width: '100%' }}
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default EventFormPage;
